<script>
import parse from 'date-fns/parse';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import addDays from 'date-fns/addDays';
import { generateTimeSlots } from '../helpers/businessHour';

const timeSlots = generateTimeSlots(30);

export default {
  components: {},
  props: {
    dayName: {
      type: String,
      required: true,
    },
    timeSlot: {
      type: Object,
      default: () => ({
        from: '',
        to: '',
      }),
    },
  },
  emits: ['update'],
  computed: {
    fromTimeSlots() {
      return timeSlots;
    },
    toTimeSlots() {
      return timeSlots.filter(slot => slot !== '00:00');
    },
    breakFromTimeSlots() {
      return timeSlots;
    },
    breakToTimeSlots() {
      return timeSlots.filter(slot => slot !== '00:00');
    },
    isDayEnabled: {
      get() {
        return Boolean(this.timeSlot.from && this.timeSlot.to);
      },
      set(value) {
        if (value) {
          this.updateSlot(
            {
              from: timeSlots[0],
              to: timeSlots[16],
              openAllDay: false,
              hasBreak: false,
              breakFrom: '',
              breakTo: '',
            },
            { validate: true }
          );
        } else {
          this.updateSlot(
            {
              from: '',
              to: '',
              openAllDay: false,
              hasBreak: false,
              breakFrom: '',
              breakTo: '',
              valid: false,
            },
            { validate: false }
          );
        }
      },
    },
    fromTime: {
      get() {
        return this.timeSlot.from;
      },
      set(value) {
        this.updateSlot({ from: value });
      },
    },
    toTime: {
      get() {
        return this.timeSlot.to;
      },
      set(value) {
        this.updateSlot({ to: value });
      },
    },
    breakFromTime: {
      get() {
        return this.timeSlot.breakFrom;
      },
      set(value) {
        this.updateSlot({ breakFrom: value });
      },
    },
    breakToTime: {
      get() {
        return this.timeSlot.breakTo;
      },
      set(value) {
        this.updateSlot({ breakTo: value });
      },
    },
    hasLunchBreak: {
      get() {
        return Boolean(this.timeSlot.hasBreak);
      },
      set(value) {
        if (value) {
          const defaults = this.getDefaultBreakWindow({
            ...this.timeSlot,
            hasBreak: true,
          });

          if (!defaults.breakFrom || !defaults.breakTo) {
            this.updateSlot(
              {
                hasBreak: false,
                breakFrom: '',
                breakTo: '',
              },
              { validate: true }
            );
          } else {
            this.updateSlot(
              {
                hasBreak: true,
                breakFrom: defaults.breakFrom,
                breakTo: defaults.breakTo,
              },
              { validate: true }
            );
          }
        } else {
          this.updateSlot(
            {
              hasBreak: false,
              breakFrom: '',
              breakTo: '',
            },
            { validate: true }
          );
        }
      },
    },
    totalHours() {
      if (this.timeSlot.openAllDay) return '24h';
      const minutes = this.getWorkingMinutes(this.timeSlot);
      if (minutes <= 0) return '0m';

      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      return [hours && `${hours}h`, remainingMinutes && `${remainingMinutes}m`]
        .filter(Boolean)
        .join(' ');
    },
    validationErrorKey() {
      if (!this.timeSlot.valid) {
        return this.timeSlot.hasBreak
          ? 'INBOX_MGMT.BUSINESS_HOURS.DAY.BREAK_VALIDATION_ERROR'
          : 'INBOX_MGMT.BUSINESS_HOURS.DAY.VALIDATION_ERROR';
      }
      return null;
    },
    hasError() {
      return Boolean(this.validationErrorKey);
    },
    isOpenAllDay: {
      get() {
        return this.timeSlot.openAllDay;
      },
      set(value) {
        if (value) {
          this.updateSlot(
            {
              from: '00:00',
              to: '23:59',
              openAllDay: value,
              hasBreak: false,
              breakFrom: '',
              breakTo: '',
            },
            { validate: true }
          );
        } else {
          this.updateSlot(
            {
              from: '09:00',
              to: '17:00',
              openAllDay: value,
              hasBreak: false,
              breakFrom: '',
              breakTo: '',
            },
            { validate: true }
          );
        }
      },
    },
  },
  methods: {
    updateSlot(changes, { validate = true } = {}) {
      const merged = { ...this.timeSlot, ...changes };
      const normalized = this.normalizeBreakWithinSlot(merged);
      const valid = validate
        ? this.calculateValidity(normalized)
        : (normalized.valid ?? false);

      this.$emit('update', { ...normalized, valid });
    },
    toComparableDate(value, { isEnd = false } = {}) {
      if (!value) return null;

      const parsed = parse(value, 'HH:mm', new Date());
      if (isEnd && value === '00:00') {
        return addDays(parsed, 1);
      }

      return parsed;
    },
    getWorkingMinutes(slot) {
      if (slot.openAllDay) return 24 * 60;

      const start = this.toComparableDate(slot.from);
      const end = this.toComparableDate(slot.to, { isEnd: true });
      if (!start || !end) return 0;

      let minutes = differenceInMinutes(end, start);

      if (slot.hasBreak && slot.breakFrom && slot.breakTo) {
        const breakStart = this.toComparableDate(slot.breakFrom);
        const breakEnd = this.toComparableDate(slot.breakTo, { isEnd: true });

        if (breakStart && breakEnd) {
          minutes -= differenceInMinutes(breakEnd, breakStart);
        }
      }

      return minutes;
    },
    calculateValidity(slot) {
      if (slot.openAllDay) return true;
      if (!slot.from || !slot.to) return false;

      const start = this.toComparableDate(slot.from);
      const end = this.toComparableDate(slot.to, { isEnd: true });
      if (!start || !end) return false;
      if (differenceInMinutes(end, start) <= 0) return false;

      if (!slot.hasBreak) return true;
      if (!slot.breakFrom || !slot.breakTo) return false;

      const breakStart = this.toComparableDate(slot.breakFrom);
      const breakEnd = this.toComparableDate(slot.breakTo, { isEnd: true });
      if (!breakStart || !breakEnd) return false;

      if (differenceInMinutes(breakEnd, breakStart) <= 0) return false;
      if (differenceInMinutes(breakStart, start) <= 0) return false;
      if (differenceInMinutes(end, breakEnd) <= 0) return false;

      return true;
    },
    normalizeBreakWithinSlot(slot) {
      const normalized = {
        ...slot,
        openAllDay: Boolean(slot.openAllDay),
      };

      if (normalized.openAllDay || !normalized.from || !normalized.to) {
        return {
          ...normalized,
          hasBreak: false,
          breakFrom: '',
          breakTo: '',
        };
      }

      if (!normalized.hasBreak) {
        return {
          ...normalized,
          hasBreak: false,
          breakFrom: '',
          breakTo: '',
        };
      }

      const fromIndex = timeSlots.indexOf(normalized.from);
      const toIndex = timeSlots.indexOf(normalized.to);
      if (fromIndex === -1 || toIndex === -1 || toIndex - fromIndex <= 2) {
        return {
          ...normalized,
          hasBreak: false,
          breakFrom: '',
          breakTo: '',
        };
      }

      const breakFromIndex = timeSlots.indexOf(normalized.breakFrom);
      const breakToIndex = timeSlots.indexOf(normalized.breakTo);

      if (
        breakFromIndex === -1 ||
        breakToIndex === -1 ||
        breakFromIndex <= fromIndex ||
        breakToIndex >= toIndex ||
        breakToIndex <= breakFromIndex
      ) {
        const defaults = this.getDefaultBreakWindow(normalized);
        if (!defaults.breakFrom || !defaults.breakTo) {
          return {
            ...normalized,
            hasBreak: false,
            breakFrom: '',
            breakTo: '',
          };
        }

        return {
          ...normalized,
          breakFrom: defaults.breakFrom,
          breakTo: defaults.breakTo,
        };
      }

      return normalized;
    },
    getDefaultBreakWindow(slot) {
      const fromIndex = timeSlots.indexOf(slot.from);
      const toIndex = timeSlots.indexOf(slot.to);
      if (fromIndex === -1 || toIndex === -1 || toIndex - fromIndex <= 2) {
        return { breakFrom: '', breakTo: '' };
      }

      const range = toIndex - fromIndex;
      const midpoint = fromIndex + Math.floor(range / 2);
      const breakFromIndex = Math.min(
        Math.max(fromIndex + 2, midpoint - 1),
        toIndex - 2
      );
      const breakToIndex = Math.min(breakFromIndex + 2, toIndex - 1);

      return {
        breakFrom: timeSlots[breakFromIndex],
        breakTo: timeSlots[breakToIndex],
      };
    },
  },
};
</script>

<template>
  <div
    class="day-wrap flex py-2 gap-1 items-center px-0 min-h-[3rem] box-content border-b border-solid border-n-weak"
  >
    <div class="checkbox-wrap flex items-center">
      <input
        v-model="isDayEnabled"
        name="enable-day"
        class="m-0"
        type="checkbox"
        :title="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.ENABLE')"
      />
    </div>
    <div
      class="day flex items-center py-0 px-3 text-sm font-medium flex-shrink-0 min-w-28"
    >
      <span>{{ dayName }}</span>
    </div>
    <div
      v-if="isDayEnabled"
      class="flex flex-col flex-shrink-0 flex-grow relative"
    >
      <div class="flex items-center flex-shrink-0 flex-grow gap-3">
        <div
          class="checkbox-wrap flex items-center open-all-day mr-6 min-w-[11rem]"
        >
          <input
            v-model="isOpenAllDay"
            name="enable-open-all-day"
            class="enable-checkbox text-sm font-medium"
            type="checkbox"
            :title="$t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')"
          />
          <span class="text-sm font-medium ml-1">{{
            $t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')
          }}</span>
        </div>
        <multiselect
          v-model="fromTime"
          :options="fromTimeSlots"
          deselect-label=""
          select-label=""
          selected-label=""
          :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
          :allow-empty="false"
          :disabled="isOpenAllDay"
        />
        <div class="separator-icon flex items-center py-0 px-0">
          <fluent-icon icon="subtract" type="solid" size="16" />
        </div>
        <multiselect
          v-model="toTime"
          :options="toTimeSlots"
          deselect-label=""
          select-label=""
          selected-label=""
          :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
          :allow-empty="false"
          :disabled="isOpenAllDay"
        />
      </div>
      <div v-if="!isOpenAllDay" class="mt-3 flex flex-col gap-2">
        <div class="flex items-start flex-shrink-0 flex-grow gap-3">
          <div class="checkbox-wrap flex items-center mr-6 min-w-[11rem]">
            <input
              v-model="hasLunchBreak"
              name="enable-lunch-break"
              class="enable-checkbox text-sm font-medium"
              type="checkbox"
              :title="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.LUNCH_BREAK')"
            />
            <span class="text-sm font-medium ml-1">
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.LUNCH_BREAK') }}
            </span>
          </div>
          <div class="flex flex-grow items-center gap-3">
            <template v-if="hasLunchBreak">
              <multiselect
                v-model="breakFromTime"
                :options="breakFromTimeSlots"
                deselect-label=""
                select-label=""
                selected-label=""
                :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
                :allow-empty="false"
              />
              <div class="separator-icon flex items-center py-0 px-0">
                <fluent-icon icon="subtract" type="solid" size="16" />
              </div>
              <multiselect
                v-model="breakToTime"
                :options="breakToTimeSlots"
                deselect-label=""
                select-label=""
                selected-label=""
                :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
                :allow-empty="false"
              />
            </template>
            <template v-else>
              <div
                class="flex flex-grow items-center gap-3 invisible pointer-events-none"
                aria-hidden="true"
              >
                <div class="w-[7.5rem] h-10" />
                <div class="separator-icon flex items-center py-0 px-0">
                  <fluent-icon icon="subtract" type="solid" size="16" />
                </div>
                <div class="w-[7.5rem] h-10" />
              </div>
            </template>
          </div>
        </div>
        <p v-if="hasLunchBreak" class="text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.LUNCH_BREAK_HELP') }}
        </p>
      </div>
      <div v-if="validationErrorKey" class="date-error pt-1">
        <span class="error text-xs text-n-ruby-9">
          {{ $t(validationErrorKey) }}
        </span>
      </div>
    </div>
    <div
      v-else
      class="flex items-center flex-shrink-0 flex-grow text-sm text-n-slate-11"
    >
      <span>
        {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.UNAVAILABLE') }}
      </span>
    </div>
    <div class="flex items-center self-stretch pl-4">
      <span
        v-if="isDayEnabled && !hasError"
        class="label bg-n-brand/10 dark:bg-n-brand/30 text-n-blue-text text-xs inline-block px-2 py-1 rounded-lg cursor-default whitespace-nowrap"
      >
        {{ totalHours }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.day-wrap::v-deep .multiselect {
  @apply m-0 w-[7.5rem];

  > .multiselect__tags {
    @apply pl-3;

    .multiselect__single {
      @apply text-sm leading-6 py-2 px-0;
    }
  }
}
</style>
